import {
  CARE_LEVELS,
  DOCS_FORM_NAMES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  ICD_CODE_CHARACTER_LIMIT,
  INFECTIONS_CHARACTER_LIMIT,
} from "core/schemas/shared";
import { DRVMedicalFormData } from "core/types";
import {
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
} from "ds/ui";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormFieldConfigProps } from "..";
import { SourceWrapper } from "../../utils";

export const CareLevelSelectField = ({
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const getOntology = useGetOntology();
  const carelevels = [
    CARE_LEVELS.NONE,
    CARE_LEVELS.ONE,
    CARE_LEVELS.TWO,
    CARE_LEVELS.THREE,
    CARE_LEVELS.FOUR,
    CARE_LEVELS.FIVE,
  ];

  const items = useMemo(
    () =>
      carelevels.map((carelevel) => ({
        id: carelevel,
        label: getOntology({ key: carelevel, type: ONTOLOGIES.CARE_LEVEL }),
        value: carelevel,
      })),
    [getOntology],
  );

  return (
    <ControlledSelect
      items={items}
      label={
        translations.patientForms.drvMedicalForm.sectionFive
          .carelevelDescription
      }
      name="carelevel"
      renderItem={(item) => item.label}
    />
  );
};

export const OtherMeasuresField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const { setValue } = useFormContext<DRVMedicalFormData>();
  const otherMeasures = useWatch({ name: "other_measures" });

  useEffect(() => {
    if (otherMeasures !== PREDICAMENT_STATE_YES) {
      setValue("further_therapeutic_diagnostic_measures_description", "");
      setValue("further_therapeutic_diagnostic_measures_from", undefined);
      setValue("further_therapeutic_diagnostic_measures_to", undefined);
      setValue(
        "further_therapeutic_diagnostic_measures_finished_on",
        undefined,
      );
    }
  }, [otherMeasures, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <FurtherTherapeuticMeasuresSubForm
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvMedicalForm.sectionTwo.otherMeasures}
      name="other_measures"
      options={options}
      size="sm"
    />
  );
};

const FurtherTherapeuticMeasuresSubForm = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  return (
    <>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="further_therapeutic_diagnostic_measures_description"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledTextarea
          label={
            translations.patientForms.drvMedicalForm.sectionTwo
              .descriptionMeasures
          }
          name="further_therapeutic_diagnostic_measures_description"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="further_therapeutic_diagnostic_measures_from"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledDateInput
          label={translations.patientForms.drvMedicalForm.sectionTwo.fromDate}
          name="further_therapeutic_diagnostic_measures_from"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="further_therapeutic_diagnostic_measures_to"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledDateInput
          label={translations.patientForms.drvMedicalForm.sectionTwo.untilDate}
          name="further_therapeutic_diagnostic_measures_to"
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="further_therapeutic_diagnostic_measures_finished_on"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledDateInput
          label={translations.patientForms.drvMedicalForm.sectionTwo.endsOnDate}
          name="further_therapeutic_diagnostic_measures_finished_on"
        />
      </SourceWrapper>
    </>
  );
};

export const SecondaryDiagnosisSelectedField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const { setValue } = useFormContext<DRVMedicalFormData>();
  const secondaryDiagnosisSelected = useWatch({
    name: "secondary_diagnosis_selected",
  });

  useEffect(() => {
    if (secondaryDiagnosisSelected !== PREDICAMENT_STATE_YES) {
      setValue("icd_code_2_code", "");
      setValue("icd_code_3_code", "");
      setValue("secondary_diagnosis", "");
    }
  }, [secondaryDiagnosisSelected, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <SecondaryDiagnosisSubForm
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={
        translations.patientForms.drvMedicalForm.sectionThree
          .descriptionIllnessess
      }
      name="secondary_diagnosis_selected"
      options={options}
      size="sm"
    />
  );
};

const SecondaryDiagnosisSubForm = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  return (
    <>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="icd_code_2_code"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={
            translations.patientForms.drvMedicalForm.sectionThree.diagnosisIcd
          }
          maxLength={ICD_CODE_CHARACTER_LIMIT}
          name="icd_code_2_code"
          placeholder=" "
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="icd_code_3_code"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={
            translations.patientForms.drvMedicalForm.sectionThree.diagnosisIcd
          }
          maxLength={ICD_CODE_CHARACTER_LIMIT}
          name="icd_code_3_code"
          placeholder=" "
        />
      </SourceWrapper>
      <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
        fieldName="secondary_diagnosis"
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <ControlledInput
          label={
            translations.patientForms.drvMedicalForm.sectionThree.nyhaStages
          }
          name="secondary_diagnosis"
          placeholder=" "
        />
      </SourceWrapper>
    </>
  );
};

export const InfectionsSelectedField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const { setValue } = useFormContext<DRVMedicalFormData>();
  const infectionsSelected = useWatch({ name: "infections_selected" });

  useEffect(() => {
    if (infectionsSelected !== PREDICAMENT_STATE_YES) {
      setValue("infections", "");
    }
  }, [infectionsSelected, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <InfectionsSubForm
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvMedicalForm.sectionFive.germs}
      name="infections_selected"
      options={options}
      size="sm"
    />
  );
};

const InfectionsSubForm = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
      fieldName="infections"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledInput
        label={
          translations.patientForms.drvMedicalForm.sectionFive.germsDescription
        }
        maxLength={INFECTIONS_CHARACTER_LIMIT}
        name="infections"
        placeholder=" "
      />
    </SourceWrapper>
  );
};

export const NeedsCatheterSelectedField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const { setValue } = useFormContext<DRVMedicalFormData>();
  const needsCatheterSelected = useWatch({ name: "needs_catheter_selected" });

  useEffect(() => {
    if (needsCatheterSelected !== PREDICAMENT_STATE_YES) {
      setValue("needs_catheter", "");
    }
  }, [needsCatheterSelected, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <NeedsCatheterSubForm
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvMedicalForm.sectionFive.catheters}
      name="needs_catheter_selected"
      options={options}
      size="sm"
    />
  );
};

const NeedsCatheterSubForm = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
      fieldName="needs_catheter"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledTextarea
        label={
          translations.patientForms.drvMedicalForm.sectionFive
            .cathetersDescription
        }
        name="needs_catheter"
      />
    </SourceWrapper>
  );
};

export const OrientationDisorientedSelectedField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const { setValue } = useFormContext<DRVMedicalFormData>();
  const orientationDisorientedSelected = useWatch({
    name: "orientation_disoriented_selected",
  });

  useEffect(() => {
    if (orientationDisorientedSelected !== PREDICAMENT_STATE_NO) {
      setValue("orientation_disoriented", "");
    }
  }, [orientationDisorientedSelected, setValue]);

  const options = [
    {
      label: translations.actions.no,
      subForm: () => (
        <OrientationDisorientedSubForm
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={translations.patientForms.drvMedicalForm.sectionFive.orientation}
      name="orientation_disoriented_selected"
      options={options}
      size="sm"
    />
  );
};

const OrientationDisorientedSubForm = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  return (
    <SourceWrapper<typeof DOCS_FORM_NAMES.DRV_MEDICAL>
      fieldName="orientation_disoriented"
      formName={formName}
      isIconOnly
      sourceInformation={sourceInformation}
    >
      <ControlledTextarea
        label={
          translations.patientForms.drvMedicalForm.sectionFive
            .orientationDescription
        }
        name="orientation_disoriented"
      />
    </SourceWrapper>
  );
};

export const CarelevelSelectedField = ({
  auction,
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.DRV_MEDICAL>) => {
  const { setValue } = useFormContext<DRVMedicalFormData>();
  const carelevelSelected = useWatch({ name: "carelevel_selected" });

  useEffect(() => {
    if (carelevelSelected !== PREDICAMENT_STATE_YES) {
      setValue("carelevel", undefined);
    }
  }, [carelevelSelected, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <CareLevelSelectField
          auction={auction}
          formName={formName}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroup
      className="w-full"
      label={
        translations.patientForms.drvMedicalForm.sectionFive.carelevelLabel
      }
      name="carelevel_selected"
      options={options}
      size="sm"
    />
  );
};
